/**
 * RNNoise processor for background noise suppression
 * Uses AudioWorklet for efficient processing
 */

import { initRNNoiseWorklet, createRNNoiseNode } from './rnnoise-wasm-loader';

/**
 * Apply RNNoise processing to a LiveKit audio track
 * @param {LocalAudioTrack} track - The LiveKit audio track to process
 * @returns {Promise<boolean>} - Whether the processing was successfully applied
 */
export async function applyRNNoiseToTrack(track) {
  if (!track || !track.mediaStreamTrack) {
    console.warn('No valid audio track provided for RNNoise processing');
    return false;
  }

  try {
    // Create audio context
    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();

    // Initialize the RNNoise audio worklet
    const workletInitialized = await initRNNoiseWorklet(audioContext);
    if (!workletInitialized) {
      console.error('Failed to initialize RNNoise audio worklet');
      return false;
    }

    // Create source from the track's MediaStreamTrack
    const mediaStream = new MediaStream([track.mediaStreamTrack]);
    const source = audioContext.createMediaStreamSource(mediaStream);

    // Create a destination for the processed audio
    const destination = audioContext.createMediaStreamDestination();

    // Create the RNNoise processor node
    const rnnoiseNode = createRNNoiseNode(audioContext);
    if (!rnnoiseNode) {
      console.error('Failed to create RNNoise processor node');
      return false;
    }

    // Create a gain node to control volume
    const gainNode = audioContext.createGain();
    gainNode.gain.value = 1.0;

    // Connect the audio processing graph
    source.connect(rnnoiseNode);
    rnnoiseNode.connect(gainNode);
    gainNode.connect(destination);

    // Replace the original track with the processed one
    const processedTrack = destination.stream.getAudioTracks()[0];

    // Store references for cleanup
    if (!track._rnnoiseProcessing) {
      track._rnnoiseProcessing = {
        context: audioContext,
        nodes: [source, rnnoiseNode, gainNode],
        destination: destination,
        originalTrack: track.mediaStreamTrack,
      };
    }

    // Apply the processed track to the original
    await track.setMediaStreamTrack(processedTrack);

    console.log('RNNoise processing applied to track');
    return true;
  } catch (error) {
    console.error('Error applying RNNoise processing:', error);
    return false;
  }
}

/**
 * Stop RNNoise processing and restore the original track
 * @param {LocalAudioTrack} track - The track with RNNoise processing applied
 * @returns {Promise<boolean>} - Whether the processing was successfully stopped
 */
export async function stopRNNoiseProcessing(track) {
  if (!track || !track._rnnoiseProcessing) {
    return false;
  }

  try {
    const processing = track._rnnoiseProcessing;

    // Disconnect the audio processing graph
    if (processing.nodes) {
      processing.nodes.forEach((node) => {
        if (node && node.disconnect) {
          node.disconnect();
        }
      });
    }

    // Close the audio context
    if (processing.context) {
      processing.context.close();
    }

    // Restore the original track
    if (processing.originalTrack) {
      await track.setMediaStreamTrack(processing.originalTrack);
    }

    // Clean up
    delete track._rnnoiseProcessing;

    console.log('RNNoise processing stopped');
    return true;
  } catch (error) {
    console.error('Error stopping RNNoise processing:', error);
    return false;
  }
}
